import React, { useEffect, useState } from 'react';
import { Box, Flex, TextV2 as Text, InputV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { FilterFacet } from '@apps/stays/components/filters/FilterFacet';
import { StyledSlider } from '@apps/stays/components/filters/StyledSlider';
import { PriceFilterSkeleton } from './FiltersPanelSkeleton';
import { useTranslation } from '@shared/core';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { PriceDistributionChart } from './PriceDistributionChart';
import { PRICE_RANGE, getCurrencySymbol, formatPriceWithCurrency } from '../../../constants';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

type PriceRangeFacetProps = Readonly<{
  title: string;
  priceRange: readonly [number, number];
  onPriceRangeChange: (value: number | readonly number[]) => void;
  isLoading?: boolean;
  prices?: ReadonlyArray<number>;
  currencyCode: string;
  navigatorLanguage: string;
  dynamicPriceMax: number;
  activeColor?: string;
}>;

const priceInputOverrides = {
  StartElement: { props: { paddingX: pxToRem(8) } },
  Input: { props: { paddingY: 0, paddingRight: 0 } },
} as const;

export const PriceRangeFacet: React.FC<PriceRangeFacetProps> = ({
  title,
  priceRange,
  onPriceRangeChange,
  isLoading,
  prices = [],
  currencyCode,
  navigatorLanguage,
  dynamicPriceMax,
  activeColor,
}) => {
  const { tq } = useTranslation('accommodationsConsole');
  const currencySymbol = getCurrencySymbol(currencyCode, navigatorLanguage);
  const minFormatted = formatPriceWithCurrency(PRICE_RANGE.MIN, currencyCode, navigatorLanguage);
  const maxFormatted = formatPriceWithCurrency(dynamicPriceMax, currencyCode, navigatorLanguage);
  const formattedRangeDescription = tq`${minFormatted} → ${maxFormatted}+`;

  const [isMinCleared, setIsMinCleared] = useState(false);
  const [isMaxCleared, setIsMaxCleared] = useState(false);

  const [minInput, setMinInput] = useState(priceRange[0].toString());
  const [maxInput, setMaxInput] = useState(priceRange[1].toString());

  const handleMinChange = useEventCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMinInput(value);
    setIsMinCleared(value === '');

    const numValue = value === '' ? PRICE_RANGE.MIN : parseInt(value, 10);
    if (value === '' || !isNaN(numValue)) {
      onPriceRangeChange([numValue, priceRange[1]]);
    }
  });

  const handleMaxChange = useEventCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMaxInput(value);
    setIsMaxCleared(value === '');

    const numValue = value === '' ? dynamicPriceMax : parseInt(value, 10);
    if (value === '' || !isNaN(numValue)) {
      onPriceRangeChange([priceRange[0], numValue]);
    }
  });

  useEffect(() => {
    if (!isMinCleared || (isMinCleared && priceRange[0] !== PRICE_RANGE.MIN)) {
      setMinInput(priceRange[0].toString());
      setIsMinCleared(false);
    }
    if (!isMaxCleared || (isMaxCleared && priceRange[1] !== dynamicPriceMax)) {
      setMaxInput(priceRange[1].toString());
      setIsMaxCleared(false);
    }
  }, [priceRange, isMinCleared, isMaxCleared, dynamicPriceMax]);

  return (
    <SkeletonGroup
      isReady={!isLoading}
      placeholder={<PriceFilterSkeleton />}
    >
      <Box maxWidth={{ _: pxToRem(222), sm: 'full' }}>
        <FilterFacet
          title={title}
          description={formattedRangeDescription}
        >
          <Flex
            gap={3}
            alignItems="center"
            marginTop={5}
          >
            <Box>
              <Text
                typographyVariant="label3"
                marginBottom={3}
              >
                {tq`Min`}
              </Text>
              <InputV2
                type="number"
                inputMode="numeric"
                value={minInput}
                onChange={handleMinChange}
                placeholder={tq`Min`}
                startElement={<Text typographyVariant="body2">{currencySymbol}</Text>}
                overrides={priceInputOverrides}
                min={0}
              />
            </Box>
            <Text
              marginTop={pxToRem(24)}
              aria-hidden="true"
            >
              -
            </Text>
            <Box>
              <Text
                typographyVariant="label3"
                marginBottom={3}
              >
                {tq`Max`}
              </Text>
              <InputV2
                type="number"
                inputMode="numeric"
                value={maxInput}
                onChange={handleMaxChange}
                placeholder={tq`Max`}
                startElement={<Text typographyVariant="body2">{currencySymbol}</Text>}
                overrides={priceInputOverrides}
                min={0}
              />
            </Box>
          </Flex>
          <Box marginTop={pxToRem(24)}>
            <Box marginBottom={pxToRem(-12)}>
              <PriceDistributionChart
                prices={prices}
                maxPrice={dynamicPriceMax}
                accentColor={activeColor}
              />
            </Box>
            <StyledSlider
              value={priceRange}
              onChange={onPriceRangeChange}
              min={PRICE_RANGE.MIN}
              max={dynamicPriceMax}
              className="slider"
              trackClassName="track"
              thumbClassName="thumb"
              $activeColor={activeColor}
            />
          </Box>
        </FilterFacet>
      </Box>
    </SkeletonGroup>
  );
};
